import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';

import { useOpenerDescalce } from '../api/useOpenerDescalce';
import type { OpenerDescalceRow } from '../api/useOpenerDescalce';
import {
  DEFAULT_DESCALCE_FILTERS,
  applyDescalceFilters,
  computeDescalceTotals,
  type DescalceFilterState
} from '../components/descalce/descalceFilters';
import {
  DEFAULT_DESCALCE_SORT,
  sortDescalceRows,
  type DescalceSortColumn,
  type DescalceSortState
} from '../components/descalce/descalceSort';
import {
  useDescalceFreshnessFrame,
  useDescalceStale
} from '../components/descalce/descalceFreshness';
import { resolveDescalceViewState } from '../components/descalce/descalceViewState';
import { DescalceHeaderStrip } from '../components/descalce/DescalceHeaderStrip';
import { DescalceFilterBar } from '../components/descalce/DescalceFilterBar';
import { DescalceEmptyState } from '../components/descalce/DescalceEmptyState';
import {
  BeliefCell,
  DescalceCell,
  DiffCell,
  GoCell,
  GroupCell,
  RuleCell,
  SideCell,
  VenueCountCell
} from '../components/descalce/DescalceCells';

interface ColumnDef {
  key: string;
  label: string;
  sort?: DescalceSortColumn;
  align?: 'left' | 'right' | 'center';
}

const COLUMNS: ColumnDef[] = [
  { key: 'descalce', label: 'Descalce', sort: 'descalce', align: 'right' },
  { key: 'side', label: 'Side' },
  { key: 'group', label: 'Group', sort: 'group' },
  { key: 'market', label: 'Market', sort: 'pairName' },
  { key: 'polyYes', label: 'Poly Y', sort: 'polyYes', align: 'right' },
  { key: 'polyNo', label: 'Poly N', sort: 'polyNo', align: 'right' },
  { key: 'kalshiYes', label: 'Kalshi Y', sort: 'kalshiYes', align: 'right' },
  { key: 'kalshiNo', label: 'Kalshi N', sort: 'kalshiNo', align: 'right' },
  { key: 'belief', label: 'Belief', sort: 'belief', align: 'right' },
  { key: 'diff', label: 'Diff', sort: 'divergence', align: 'right' },
  { key: 'rule', label: 'Rule', sort: 'ruleStatus' },
  { key: 'go', label: 'Go', align: 'center' }
];

const fmtTime = (ms: number): string => new Date(ms).toLocaleTimeString();

export const DescalcePage = () => {
  const { data, isLoading, isError, error } = useOpenerDescalce();

  const [filters, setFilters] = useState<DescalceFilterState>(
    DEFAULT_DESCALCE_FILTERS
  );
  const [sort, setSort] = useState<DescalceSortState>(DEFAULT_DESCALCE_SORT);

  const frame = useDescalceFreshnessFrame(data?.computedAt);
  const stale = useDescalceStale(frame, data?.staleMs);

  const viewState = resolveDescalceViewState({
    isLoading,
    isError,
    error,
    data
  });

  const visibleRows: OpenerDescalceRow[] = useMemo(() => {
    const rows = data?.rows ?? [];
    return sortDescalceRows(applyDescalceFilters(rows, filters), sort);
  }, [data, filters, sort]);

  const totals = useMemo(() => computeDescalceTotals(visibleRows), [visibleRows]);

  const toggleSort = (column: DescalceSortColumn) => {
    setSort(prev =>
      prev.column === column
        ? { column, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column, direction: 'desc' }
    );
  };

  const lastSuccessLabel = frame ? fmtTime(frame.computedAt) : null;

  return (
    <Box
      sx={{
        px: 2,
        pt: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1, flexShrink: 0 }}
      >
        <BalanceOutlinedIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Descalces
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
          Cross-group opener leg imbalance — read-only triage, largest naked leg
          first.
        </Typography>
      </Stack>

      {stale && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1, flexShrink: 0 }}>
          Stale — last read {lastSuccessLabel ?? 'unknown'}. Showing the last
          known snapshot; this does NOT substitute another data source.
        </Alert>
      )}

      <DescalceFilterBar filters={filters} onChange={setFilters} />
      <DescalceHeaderStrip totals={totals} snapshot={data} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.align ?? 'left'}
                    sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                  >
                    {col.sort ? (
                      <TableSortLabel
                        active={sort.column === col.sort}
                        direction={sort.column === col.sort ? sort.direction : 'desc'}
                        onClick={() => toggleSort(col.sort as DescalceSortColumn)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {viewState !== 'rows' ? (
                <DescalceEmptyState state={viewState} colSpan={COLUMNS.length} />
              ) : visibleRows.length === 0 ? (
                // The snapshot has rows, but the active client-side filters
                // (search / min descalce / toggles) hide all of them — a
                // distinct message from `DescalceEmptyState`'s four
                // backend-driven states, so "genuinely no positions" and
                // "filtered down to zero" never read the same.
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    align="center"
                    data-descalce-empty-state="filtered-empty"
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: 'grey.500', py: 2 }}>
                      No rows match the current filters — {data?.rows.length ?? 0} row(s) in the snapshot are hidden.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map(row => (
                  <TableRow key={row.ruleId} hover data-rule-id={row.ruleId}>
                    <TableCell align="right">
                      <DescalceCell row={row} />
                    </TableCell>
                    <TableCell>
                      <SideCell row={row} />
                    </TableCell>
                    <TableCell>
                      <GroupCell group={row.group} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography sx={{ fontSize: '0.8rem' }} noWrap title={row.pairName}>
                        {row.pairName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <VenueCountCell value={row.venue?.polyYes} />
                    </TableCell>
                    <TableCell align="right">
                      <VenueCountCell value={row.venue?.polyNo} />
                    </TableCell>
                    <TableCell align="right">
                      <VenueCountCell value={row.venue?.kalshiYes} />
                    </TableCell>
                    <TableCell align="right">
                      <VenueCountCell value={row.venue?.kalshiNo} />
                    </TableCell>
                    <TableCell align="right">
                      <BeliefCell row={row} />
                    </TableCell>
                    <TableCell align="right">
                      <DiffCell row={row} />
                    </TableCell>
                    <TableCell>
                      <RuleCell row={row} />
                    </TableCell>
                    <TableCell align="center">
                      <GoCell row={row} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
